/** Helpers + response normalization for GET /api/dashboard/summary */

const CHASE_MODALITY_FALLBACK = [
  { key: "ct_report", label: "CT Report" },
  { key: "ct_images", label: "Raw CT" },
  { key: "mri", label: "MRI" },
  { key: "3mensio_ct", label: "3Mensio" },
  { key: "pre_echo", label: "Pre-Echo" },
  { key: "post_echo", label: "Post-Echo" },
  { key: "fluoro", label: "Fluoro" },
];

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toStr = (value, fallback = "") => {
  if (value == null) return fallback;
  const s = String(value).trim();
  return s || fallback;
};

const addDays = (date, days) => {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const formatDateOnly = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Current ISO week (Mon–Sun) as YYYY-MM-DD. */
export const getCurrentIsoWeekRange = () => {
  const now = new Date();
  const utc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const day = utc.getUTCDay() || 7;
  const monday = addDays(utc, 1 - day);
  const sunday = addDays(monday, 6);
  return { dateFrom: formatDateOnly(monday), dateTo: formatDateOnly(sunday) };
};

export const getIsoWeekNumber = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  const tmp = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
};

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

export const formatRangeLabel = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) return "";
  const parse = (s) => {
    const [y, m, d] = String(s).split("-").map(Number);
    return { y, m, d };
  };
  const a = parse(dateFrom);
  const b = parse(dateTo);
  if (!a.y || !b.y) return `${dateFrom} – ${dateTo}`;
  const left = `${a.d} ${MONTHS[a.m - 1] ?? ""}`;
  const right = `${b.d} ${MONTHS[b.m - 1] ?? ""} ${b.y}`;
  return `${left} – ${right}`.replace(/\s+/g, " ").trim();
};

const pick = (obj, keys, fallback) => {
  if (!obj || typeof obj !== "object") return fallback;
  for (const key of keys) {
    if (obj[key] != null) return obj[key];
  }
  return fallback;
};

/** Supports nested `{ value, delta }` or flat number + sibling `*_delta`. */
const normalizeKpi = (raw, fallbackValue = 0, deltaRaw = null) => {
  if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
    const value = toNum(pick(raw, ["value", "count", "total", "current"], fallbackValue));
    const previous = toNum(pick(raw, ["previous", "prev", "last", "prior"], 0));
    const delta = raw.delta != null
      ? toNum(raw.delta)
      : deltaRaw != null
        ? toNum(deltaRaw)
        : value - previous;
    return { value, previous: previous || value - delta, delta };
  }
  const value = toNum(raw, fallbackValue);
  const delta = deltaRaw != null ? toNum(deltaRaw) : 0;
  return { value, previous: value - delta, delta };
};

const toModalityKeyList = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map((m) => (typeof m === "string" ? m : m?.key)).filter(Boolean);
};

const normalizeChaseItem = (item = {}) => {
  const presentList = toModalityKeyList(
    pick(item, ["uploaded_modalities", "modalities_present", "present_modalities", "datasets_present"], []),
  );
  const missingList = toModalityKeyList(
    pick(item, ["missing_modalities", "modalities_missing"], []),
  );
  const missingKey = item.missing_modality ?? item.missing_key ?? missingList[0] ?? null;
  const hospitalRaw = item.institution_name ?? item.hospital_name ?? item.hospital ?? item.organization;
  const patientName = item.patient_name ?? item.name ?? null;

  return {
    id: item.id ?? item.patient_uhid ?? item.case_id ?? null,
    patient_uhid: item.patient_uhid ?? item.uhid ?? null,
    patient_name: patientName ? toStr(patientName) : null,
    case_id: toStr(item.case_id ?? item.caseId ?? item.patient_uhid, "—"),
    hospital_name: hospitalRaw != null && String(hospitalRaw).trim() ? toStr(hospitalRaw) : null,
    doctor_name: item.doctor_name ?? item.referring_doctor ?? item.doctor ?? null,
    days_pending: toNum(
      item.days_since_created ?? item.days_pending ?? item.days ?? item.age_days,
      0,
    ),
    modalities_present: presentList,
    missing_modalities: missingList,
    missing_modality: missingKey,
    missing_label: item.missing_label ?? item.missing ?? item.missing_dataset ?? null,
  };
};

const normalizeRep = (row = {}, index = 0) => ({
  rank: toNum(row.rank, index + 1),
  id: row.id ?? row.user_id ?? null,
  name: toStr(row.name ?? row.representative ?? row.rep_name, "—"),
  territory: toStr(row.territory ?? row.location ?? row.region, "—"),
  cases: toNum(row.cases ?? row.case_count ?? row.ct_count, 0),
  cases_wow: toNum(row.cases_wow ?? row.wow ?? row.delta_cases ?? row.cases_delta, 0),
  completeness: toNum(row.completeness ?? row.completeness_pct, 0),
  pending: toNum(row.pending ?? row.pending_datasets ?? row.pending_count ?? row.pending_cases, 0),
  is_current_user: Boolean(row.is_current_user ?? row.is_self ?? row.is_me ?? row.current),
});

const normalizeHospital = (row = {}, index = 0) => ({
  rank: toNum(row.rank, index + 1),
  id: row.id ?? row.organization_id ?? null,
  name: toStr(row.name ?? row.hospital_name ?? row.organization_name, "—"),
  cases: toNum(row.cases ?? row.case_count, 0),
  complete_cases: toNum(row.complete_cases ?? row.complete, 0),
  completeness: toNum(row.completeness ?? row.completeness_pct, 0),
  pending: toNum(row.pending ?? row.pending_cases ?? row.pending_datasets ?? row.pending_count, 0),
});

const normalizePendingByAge = (raw) => {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    "0_7": toNum(src["0_7"] ?? src.d0_7 ?? src["0-7"] ?? src.bucket_0_7, 0),
    "8_14": toNum(src["8_14"] ?? src.d8_14 ?? src["8-14"] ?? src.bucket_8_14, 0),
    "15_30": toNum(src["15_30"] ?? src.d15_30 ?? src["15-30"] ?? src.bucket_15_30, 0),
    "30_plus": toNum(src["30_plus"] ?? src.d30_plus ?? src["30+"] ?? src.bucket_30_plus, 0),
  };
};

/**
 * Normalize GET /api/dashboard/summary payloads into a stable UI shape.
 * Tolerates nested or flat field names from the live API.
 */
export const normalizeWeeklySummary = (data = {}) => {
  const kpisRaw = data.kpis ?? data.metrics ?? data.stats ?? {};
  const casesKpi = normalizeKpi(
    kpisRaw.cases ?? data.cases ?? data.case_count,
    toNum(data.cases ?? data.case_count, 0),
    kpisRaw.cases_delta ?? data.cases_delta,
  );
  const datasetsKpi = normalizeKpi(
    kpisRaw.datasets_uploaded ?? kpisRaw.datasets ?? data.datasets_uploaded,
    toNum(data.datasets_uploaded ?? data.datasets, 0),
    kpisRaw.datasets_uploaded_delta ?? kpisRaw.datasets_delta ?? data.datasets_uploaded_delta,
  );
  const pendingKpi = normalizeKpi(
    kpisRaw.pending_datasets ?? kpisRaw.pending ?? data.pending_datasets,
    toNum(data.pending_datasets ?? data.pending, 0),
    kpisRaw.pending_datasets_delta ?? kpisRaw.pending_delta ?? data.pending_datasets_delta,
  );
  const completenessKpi = normalizeKpi(
    kpisRaw.completeness_pct ?? kpisRaw.completeness ?? data.completeness_pct ?? data.completeness,
    toNum(data.completeness_pct ?? data.completeness, 0),
    kpisRaw.completeness_delta ?? data.completeness_delta,
  );
  const fullCompletenessKpi = normalizeKpi(
    kpisRaw.full_completeness_pct ?? data.full_completeness_pct,
    toNum(data.full_completeness_pct, 0),
    kpisRaw.full_completeness_delta ?? data.full_completeness_delta,
  );

  const chaseRaw = data.chase ?? data.chase_list ?? data.pending_cases ?? data.cases_missing ?? [];
  const chase = Array.isArray(chaseRaw) ? chaseRaw.map(normalizeChaseItem) : [];

  const rankingsRaw = data.rankings ?? data.rank ?? {};
  const repsRaw = (() => {
    if (Array.isArray(data.rep_leaderboard)) return data.rep_leaderboard;
    if (Array.isArray(data.leaderboard)) return data.leaderboard;
    if (Array.isArray(rankingsRaw)) return rankingsRaw;
    if (Array.isArray(rankingsRaw?.reps)) return rankingsRaw.reps;
    if (Array.isArray(rankingsRaw?.representatives)) return rankingsRaw.representatives;
    if (Array.isArray(rankingsRaw?.items)) return rankingsRaw.items;
    return [];
  })();
  const reps = repsRaw.map(normalizeRep);

  const hospitalsRaw = (() => {
    if (Array.isArray(data.hospital_leaderboard)) return data.hospital_leaderboard;
    if (Array.isArray(rankingsRaw?.hospitals)) return rankingsRaw.hospitals;
    if (Array.isArray(data.hospitals)) return data.hospitals;
    return [];
  })();
  const hospitals = hospitalsRaw.map(normalizeHospital);

  const shareRaw = data.case_share ?? data.caseShare ?? data.share ?? {};
  const ownCases = toNum(
    shareRaw.own_cases ?? shareRaw.medtronic ?? shareRaw.medtronic_count,
    0,
  );
  const competition = toNum(
    shareRaw.competition_cases ?? shareRaw.competition ?? shareRaw.competition_count,
    0,
  );
  const shareTotal = toNum(shareRaw.total_cases, ownCases + competition);
  const sharePct = shareRaw.share_pct != null
    ? toNum(shareRaw.share_pct)
    : shareRaw.medtronic_pct != null
      ? toNum(shareRaw.medtronic_pct)
      : shareTotal
        ? Math.round((ownCases / shareTotal) * 100)
        : 0;

  const userRaw = data.user ?? data.profile ?? {};
  const period = data.period ?? {};
  const modalitiesRaw = data.modalities ?? data.chase_modalities ?? null;
  let modalities = Array.isArray(modalitiesRaw) && modalitiesRaw.length
    ? modalitiesRaw.map((m) => (
      typeof m === "string"
        ? { key: m, label: m }
        : { key: m.key, label: m.label ?? m.name ?? m.key }
    ))
    : CHASE_MODALITY_FALLBACK;

  // Ensure chase uploaded/missing keys appear in the modality dots/legend
  const chaseKeys = new Set();
  chase.forEach((item) => {
    (item.modalities_present ?? []).forEach((k) => chaseKeys.add(k));
    (item.missing_modalities ?? []).forEach((k) => chaseKeys.add(k));
  });
  if (chaseKeys.size > 0) {
    const known = new Set(modalities.map((m) => m.key));
    chaseKeys.forEach((key) => {
      if (known.has(key)) return;
      const fallback = CHASE_MODALITY_FALLBACK.find((m) => m.key === key);
      modalities = [...modalities, { key, label: fallback?.label ?? key }];
    });
  }

  const dateFrom = data.date_from ?? data.from ?? period.from ?? null;
  const dateTo = data.date_to ?? data.to ?? period.to ?? null;

  return {
    date_from: dateFrom,
    date_to: dateTo,
    week_number: data.week_number ?? data.week ?? getIsoWeekNumber(dateFrom),
    user: {
      name: userRaw.name ?? null,
      role_label: userRaw.role_label ?? userRaw.role ?? null,
      territory: userRaw.territory ?? null,
      zone: userRaw.zone ?? rankingsRaw.zone ?? null,
    },
    kpis: {
      cases: casesKpi,
      datasets_uploaded: datasetsKpi,
      pending_datasets: pendingKpi,
      completeness: completenessKpi,
      full_completeness: fullCompletenessKpi,
    },
    chase,
    chase_total: toNum(
      data.chase_total ?? data.pending_count ?? kpisRaw.pending_datasets,
      chase.length,
    ),
    pending_by_age: normalizePendingByAge(data.pending_by_age ?? data.pendingByAge ?? data.age_buckets),
    rankings: {
      zone: rankingsRaw.zone ?? userRaw.zone ?? "your zone",
      reps,
      hospitals,
    },
    case_share: {
      medtronic: ownCases,
      competition,
      medtronic_pct: sharePct,
      delta_pts: toNum(
        shareRaw.share_delta_pts ?? shareRaw.delta_pts ?? shareRaw.wow_pts ?? shareRaw.delta,
        0,
      ),
      own_org_name: shareRaw.own_org_name ?? shareRaw.org_name ?? null,
    },
    modalities,
  };
};

export const formatSigned = (value, { pts = false } = {}) => {
  const n = toNum(value);
  const abs = Math.abs(n);
  const suffix = pts ? " pts" : "";
  if (n > 0) return `+${abs}${suffix}`;
  if (n < 0) return `-${abs}${suffix}`;
  return `0${suffix}`;
};
